import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AIGeneratorModal from './AIGeneratorModal';
import CategoryManager from './CategoryManager';
import ActivityLeaderboard from './ActivityLeaderboard';
import TerminalQuestLeaderboard from './TerminalQuestLeaderboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function TeacherDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [hangmanActivities, setHangmanActivities] = useState([]);
  const [linuxQuests, setLinuxQuests] = useState([]);
  const [textHunts, setTextHunts] = useState([]);
  const [odooActivities, setOdooActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classroomConnected, setClassroomConnected] = useState(false);
  const [activeTab, setActiveTab] = useState('quizzes'); // 'quizzes', 'hangman', 'terminal', 'texthunt', or 'odoo'
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiActivityType, setAiActivityType] = useState('quiz');
  const [showCreateMenu, setShowCreateMenu] = useState(null); // stores which activity menu is open
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [courses, setCourses] = useState([]);
  
  // Leaderboard modal states
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState(null);
  
  // Filter states
  const [courseFilter, setCourseFilter] = useState('all');
  const [activityTypeFilter, setActivityTypeFilter] = useState('all');
  
  // Category states
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  const fetchQuizzes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quizzes/teacher/${user.id}`);
      setQuizzes(response.data);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    }
  };

  const fetchHangmanActivities = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hangman/teacher/${user.id}`);
      setHangmanActivities(response.data);
    } catch (error) {
      console.error('Error fetching hangman activities:', error);
    }
  };

  const fetchLinuxQuests = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/linux-quest/teacher/${user.id}`);
      setLinuxQuests(response.data);
    } catch (error) {
      console.error('Error fetching linux quests:', error);
    }
  };

  const fetchTextHunts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/texthunt/teacher/${user.id}`);
      setTextHunts(response.data);
    } catch (error) {
      console.error('Error fetching texthunts:', error);
    }
  };

  const fetchOdooActivities = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/odoo-activities?teacher_id=${user.email}`);
      setOdooActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching Odoo activities:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categories/${user.id}`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAllActivities = async () => {
    setLoading(true);
    await Promise.all([
      fetchQuizzes(), 
      fetchHangmanActivities(), 
      fetchLinuxQuests(), 
      fetchTextHunts(),
      fetchOdooActivities(),
      fetchCategories()
    ]);
    setLoading(false);
  };

  const checkClassroomConnection = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/classroom/courses/${user.id}`);
      setClassroomConnected(true);
      setCourses(response.data.courses || []);
    } catch (error) {
      setClassroomConnected(false);
      setCourses([]);
    }
  };

  useEffect(() => {
    fetchAllActivities();
    checkClassroomConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter activities by category and sort alphabetically
  const filterAndSortActivities = (activities) => {
    let filtered = categoryFilter 
      ? activities.filter(activity => activity.category_id === categoryFilter)
      : activities;
    
    // Sort alphabetically by title
    return filtered.sort((a, b) => 
      (a.title || '').localeCompare(b.title || '', 'es', { sensitivity: 'base' })
    );
  };

  // Get filtered and sorted lists
  const filteredQuizzes = filterAndSortActivities(quizzes);
  const filteredHangman = filterAndSortActivities(hangmanActivities);
  const filteredQuests = filterAndSortActivities(linuxQuests);
  const filteredTextHunts = filterAndSortActivities(textHunts);
  const filteredOdooActivities = filterAndSortActivities(odooActivities);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close create menu if clicking outside
      if (showCreateMenu && !event.target.closest('.relative')) {
        setShowCreateMenu(null);
      }
      // Close import menu if clicking outside
      if (showImportMenu && !event.target.closest('.relative')) {
        setShowImportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCreateMenu, showImportMenu]);

  const handleStartSession = async (quizId) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/sessions`, {
        quiz_id: quizId,
        teacher_id: user.id
      });
      navigate(`/session/${response.data.pin}/lobby`);
    } catch (error) {
      console.error('Error creating session:', error);
      alert('Error al crear la sesión');
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    if (window.confirm('¿Estás seguro de eliminar este quiz?')) {
      try {
        await axios.delete(`${BACKEND_URL}/api/quizzes/${quizId}`);
        fetchQuizzes();
      } catch (error) {
        console.error('Error deleting quiz:', error);
        alert('Error al eliminar el quiz');
      }
    }
  };

  const handleExportXML = (quizId, quizTitle) => {
    // Open download URL in new tab
    const downloadUrl = `${BACKEND_URL}/api/quizzes/${quizId}/export-xml`;
    window.open(downloadUrl, '_blank');
  };

  const handleExportHangmanXML = (hangmanId) => {
    // Open download URL in new tab
    const downloadUrl = `${BACKEND_URL}/api/hangman/${hangmanId}/export-xml`;
    window.open(downloadUrl, '_blank');
  };

  const handleExportLinuxQuestXML = (questId) => {
    // Open download URL in new tab
    const downloadUrl = `${BACKEND_URL}/api/linux-quest/${questId}/export-xml`;
    window.open(downloadUrl, '_blank');
  };

  const handleExportTextHunt = async (texthunt) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/texthunt/${texthunt.id}/export`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      // Create download link
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `texthunt_${texthunt.title.replace(/\s+/g, '_')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert('✅ TextHunt exportado exitosamente');
    } catch (error) {
      console.error('Error exporting TextHunt:', error);
      alert('Error al exportar TextHunt: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleImportXML = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xml')) {
      alert('Por favor selecciona un archivo XML válido');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('teacher_id', user.id);
    formData.append('teacher_name', user.name);

    try {
      let response;
      if (activeTab === 'terminal') {
        // Import Terminal Quest
        response = await axios.post(`${BACKEND_URL}/api/linux-quest/import-xml`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert(`✅ Terminal Quest importado exitosamente!\n\n${response.data.levels?.length || 0} niveles importados`);
        fetchLinuxQuests();
      } else if (activeTab === 'hangman') {
        // Import Hangman
        response = await axios.post(`${BACKEND_URL}/api/hangman/import-xml`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert(`✅ Ahorcado importado exitosamente!\n\n${response.data.words_count} palabras importadas`);
        fetchHangmanActivities();
      } else {
        // Import Quiz (default)
        response = await axios.post(`${BACKEND_URL}/api/quizzes/import-xml`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: { teacher_id: user.id }
        });
        alert(`✅ Quiz importado exitosamente!\n\n${response.data.questions_count} preguntas importadas`);
        fetchQuizzes();
      }
      
      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('Error importing XML:', error);
      const errorMsg = error.response?.data?.detail || 'Error al importar el archivo XML';
      alert(`❌ Error: ${errorMsg}`);
    }
  };

  const handleImportTextHuntJSON = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      // Read file content
      const fileContent = await file.text();
      const texthuntData = JSON.parse(fileContent);
      
      // Send to backend
      const response = await axios.post(`${BACKEND_URL}/api/texthunt/import`, {
        teacher_id: user.id,
        teacher_name: user.name,
        texthunt_data: texthuntData
      });
      
      alert(`✅ TextHunt "${response.data.title}" importado exitosamente`);
      fetchTextHunts();
    } catch (error) {
      console.error('Error importing TextHunt:', error);
      const errorMsg = error.response?.data?.detail || 'Error al importar el archivo JSON';
      alert(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleDeleteHangman = async (hangmanId) => {
    if (!window.confirm('¿Estás seguro de eliminar este ahorcado?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/hangman/${hangmanId}`);
      fetchHangmanActivities();
    } catch (error) {
      console.error('Error deleting hangman:', error);
      alert('Error al eliminar el ahorcado');
    }
  };

  const handleReorderHangman = async (hangmanId, direction) => {
    try {
      await axios.post(`${BACKEND_URL}/api/hangman/${hangmanId}/reorder`, {
        direction,
        teacher_id: user.id
      });
      fetchHangmanActivities();
    } catch (error) {
      console.error('Error reordering hangman:', error);
      if (error.response?.status === 409 || error.response?.status === 400) {
        // Silently ignore "already at top/bottom" messages
        return;
      }
      alert('Error al reordenar la actividad');
    }
  };

  const handleDeleteLinuxQuest = async (questId) => {
    if (!window.confirm('¿Estás seguro de eliminar este Terminal Quest?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/linux-quest/${questId}`);
      fetchLinuxQuests();
    } catch (error) {
      console.error('Error deleting linux quest:', error);
      alert('Error al eliminar el Terminal Quest');
    }
  };

  const handleDeleteTextHunt = async (texthuntId) => {
    if (!window.confirm('¿Estás seguro de eliminar este TextHunt?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/texthunt/${texthuntId}`);
      fetchTextHunts();
    } catch (error) {
      console.error('Error deleting texthunt:', error);
      alert('Error al eliminar el TextHunt');
    }
  };

  const handleDeleteOdooActivity = async (activityId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta Actividad Odoo?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/odoo-activities/${activityId}`);
      fetchOdooActivities();
    } catch (error) {
      console.error('Error deleting Odoo activity:', error);
      alert('Error al eliminar la Actividad Odoo');
    }
  };

  const handleConnectClassroom = async () => {
    try {
      // Store user_id and user_type for callback
      localStorage.setItem('user_id_for_classroom', user.id);
      localStorage.setItem('user_type_for_classroom', 'teacher');
      
      const response = await axios.get(`${BACKEND_URL}/api/classroom/auth-url`, {
        params: { 
          teacher_id: user.id,
          user_type: 'teacher'
        }
      });
      
      // Redirect to Google OAuth
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Error connecting to Classroom:', error);
      alert('Error al conectar con Google Classroom');
    }
  };

  const handleOpenAIModal = (type) => {
    setAiActivityType(type);
    setShowAIModal(true);
  };

  const handleAISuccess = (data) => {
    alert(`✅ Actividad creada con IA correctamente! Proveedor: ${data.ai_provider}`);
    // Refresh the appropriate list
    if (aiActivityType === 'quiz') {
      fetchQuizzes();
    } else if (aiActivityType === 'hangman') {
      fetchHangmanActivities();
    } else if (aiActivityType === 'linux-quest') {
      fetchLinuxQuests();
    }
  };

  // Filter activities by course
  const filterActivitiesByCourse = (activities) => {
    if (courseFilter === 'all') return activities;
    
    return activities.filter(activity => {
      // If activity is public, show it
      if (activity.is_public) return true;
      
      // If activity has assigned_courses, check if selected course is included
      if (activity.assigned_courses && Array.isArray(activity.assigned_courses)) {
        return activity.assigned_courses.includes(courseFilter);
      }
      
      return false;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="teacher-dashboard">
      {/* Header with IMPROVED responsive design */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col gap-3">
            {/* Top Row - Logo and User Info */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-4">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-primary">QuizMaster</h1>
                <span className="text-gray-500 hidden sm:inline">|</span>
                <span className="text-xs sm:text-sm md:text-base text-gray-700">Panel del Profesor</span>
              </div>
              
              <div className="flex items-center gap-2">
                {user.picture && (
                  <img src={user.picture} alt={user.name} className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full" />
                )}
                <span className="text-gray-700 font-medium text-xs sm:text-sm md:text-base hidden sm:inline">{user.name}</span>
              </div>
            </div>
            
            {/* Bottom Row - Action Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Admin Panel Button (only for admins) */}
              {user.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium text-xs sm:text-sm"
                  title="Panel de Administración"
                >
                  <span>👑</span>
                  <span className="hidden sm:inline">Admin</span>
                </button>
              )}
              
              {/* Categories Button */}
              <button
                onClick={() => setShowCategoryManager(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium text-sm md:text-base"
                title="Gestionar categorías para organizar actividades"
              >
                <span>🗂️</span>
                Categorías
              </button>
              
              {/* Classroom Connection Status */}
              {classroomConnected ? (
                <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-green-100 text-green-700 rounded-lg text-xs sm:text-sm font-medium">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  🔗 <span className="hidden sm:inline">Classroom</span>
                </div>
              ) : (
                <button
                  onClick={handleConnectClassroom}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-xs sm:text-sm"
                  data-testid="connect-classroom-btn"
                  title="Conecta para acceder a todos tus cursos de Classroom"
                >
                  🔗 <span className="hidden sm:inline">Conectar</span> Classroom
                </button>
              )}
              
              <button
                onClick={onLogout}
                className="flex items-center gap-1 px-2 sm:px-4 py-1.5 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-xs sm:text-sm ml-auto"
                data-testid="logout-btn"
              >
                🚪 <span className="hidden sm:inline">Salir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">Bienvenido, {user.name}! 👋</h2>
          <p className="text-sm md:text-base text-gray-600">Gestiona tus actividades y crea nuevas sesiones interactivas</p>
        </div>

        {/* Unified Create Activity Section - IMPROVED */}
        <div className="mb-8 bg-white rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
            <h3 className="text-lg font-semibold text-gray-700">➕ Crear Nueva Actividad</h3>
            
            {/* Import Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowImportMenu(!showImportMenu)}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Importar Actividad
              </button>
              
              {showImportMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border z-50">
                  <div className="py-2">
                    <label className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div className="flex-1">
                        <div className="font-medium text-sm">Importar desde XML</div>
                        <div className="text-xs text-gray-500">Quiz, Hangman o Terminal Quest</div>
                      </div>
                      <input
                        type="file"
                        accept=".xml"
                        onChange={(e) => {
                          handleImportXML(e);
                          setShowImportMenu(false);
                        }}
                        className="hidden"
                        data-testid="import-xml-input"
                      />
                    </label>
                    <label className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition border-t">
                      <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <div className="flex-1">
                        <div className="font-medium text-sm">Importar TextHunt JSON</div>
                        <div className="text-xs text-gray-500">Comprensión de texto</div>
                      </div>
                      <input
                        type="file"
                        accept=".json"
                        onChange={(e) => {
                          handleImportTextHuntJSON(e);
                          setShowImportMenu(false);
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Quiz Card */}
            <div className="relative border-2 border-blue-200 rounded-lg p-4 hover:border-blue-400 transition">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">📝</span>
                <h4 className="font-semibold text-blue-700">Quiz</h4>
              </div>
              <button
                onClick={() => setShowCreateMenu(showCreateMenu === 'quiz' ? null : 'quiz')}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                data-testid="create-quiz-btn"
              >
                + Crear Quiz
              </button>
              
              {showCreateMenu === 'quiz' && (
                <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border z-10">
                  <button
                    onClick={() => {
                      navigate('/quiz/create');
                      setShowCreateMenu(null);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition flex items-center gap-2 border-b"
                  >
                    <span>✏️</span>
                    <div>
                      <div className="font-medium text-sm">Creación Manual</div>
                      <div className="text-xs text-gray-500">Escribe tus propias preguntas</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      handleOpenAIModal('quiz');
                      setShowCreateMenu(null);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition flex items-center gap-2"
                  >
                    <span>🤖</span>
                    <div>
                      <div className="font-medium text-sm">Generar con IA</div>
                      <div className="text-xs text-gray-500">Creación automática</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* Hangman Card */}
            <div className="relative border-2 border-green-200 rounded-lg p-4 hover:border-green-400 transition">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🎯</span>
                <h4 className="font-semibold text-green-700">Ahorcado</h4>
              </div>
              <button
                onClick={() => setShowCreateMenu(showCreateMenu === 'hangman' ? null : 'hangman')}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
              >
                + Crear Ahorcado
              </button>
              
              {showCreateMenu === 'hangman' && (
                <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border z-10">
                  <button
                    onClick={() => {
                      navigate('/hangman/create');
                      setShowCreateMenu(null);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition flex items-center gap-2 border-b"
                  >
                    <span>✏️</span>
                    <div>
                      <div className="font-medium text-sm">Creación Manual</div>
                      <div className="text-xs text-gray-500">Escribe tus propias palabras</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      handleOpenAIModal('hangman');
                      setShowCreateMenu(null);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition flex items-center gap-2"
                  >
                    <span>🤖</span>
                    <div>
                      <div className="font-medium text-sm">Generar con IA</div>
                      <div className="text-xs text-gray-500">Creación automática</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* Terminal Quest */}
            <div className="relative border-2 border-gray-200 rounded-lg p-4 hover:border-gray-400 transition">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">💻</span>
                <h4 className="font-semibold text-gray-700">Terminal Quest</h4>
              </div>
              <button
                onClick={() => setShowCreateMenu(showCreateMenu === 'terminal' ? null : 'terminal')}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition text-sm font-medium"
              >
                + Crear Quest
              </button>
              
              {showCreateMenu === 'terminal' && (
                <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border z-10">
                  <button
                    onClick={() => {
                      navigate('/terminal-quest/create');
                      setShowCreateMenu(null);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition flex items-center gap-2 border-b"
                  >
                    <span>✏️</span>
                    <div>
                      <div className="font-medium text-sm">Creación Manual</div>
                      <div className="text-xs text-gray-500">Diseña tus propios niveles</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      handleOpenAIModal('linux-quest');
                      setShowCreateMenu(null);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition flex items-center gap-2"
                  >
                    <span>🤖</span>
                    <div>
                      <div className="font-medium text-sm">Generar con IA</div>
                      <div className="text-xs text-gray-500">Creación automática</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* TextHunt Card */}
            <div className="relative border-2 border-orange-200 rounded-lg p-4 hover:border-orange-400 transition">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">📚</span>
                <h4 className="font-semibold text-orange-700">TextHunt</h4>
              </div>
              <button
                onClick={() => setShowCreateMenu(showCreateMenu === 'texthunt' ? null : 'texthunt')}
                className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-medium"
              >
                + Crear TextHunt
              </button>
              
              {showCreateMenu === 'texthunt' && (
                <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border z-10">
                  <button
                    onClick={() => {
                      navigate('/texthunt/create');
                      setShowCreateMenu(null);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition flex items-center gap-2 border-b"
                  >
                    <span>✏️</span>
                    <div>
                      <div className="font-medium text-sm">Creación Manual</div>
                      <div className="text-xs text-gray-500">Escribe tu texto y preguntas</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      handleOpenAIModal('texthunt');
                      setShowCreateMenu(null);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition flex items-center gap-2"
                  >
                    <span>🤖</span>
                    <div>
                      <div className="font-medium text-sm">Generar con IA</div>
                      <div className="text-xs text-gray-500">Creación automática</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* Odoo Activity Card */}
            <div className="relative border-2 border-indigo-200 rounded-lg p-4 hover:border-indigo-400 transition">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🏢</span>
                <h4 className="font-semibold text-indigo-700">Odoo ERP</h4>
              </div>
              <button
                onClick={() => navigate('/odoo-activity/create')}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
              >
                + Crear Actividad Odoo
              </button>
            </div>
          </div>
        </div>

        {/* Course Filter Section */}
        {classroomConnected && courses.length > 0 && (
          <div className="mb-6 bg-white rounded-xl shadow-sm p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtrar por Curso:
              </label>
              <select
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                className="flex-1 sm:flex-none sm:min-w-[250px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">📚 Todos los cursos</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name}
                  </option>
                ))}
              </select>
              {courseFilter !== 'all' && (
                <button
                  onClick={() => setCourseFilter('all')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Limpiar filtro
                </button>
              )}
            </div>
          </div>
        )}

        <div className="mb-6 flex gap-4 flex-wrap">
          {/* Individual Results Button */}
          <button
            onClick={() => navigate('/teacher/individual-results')}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            Resultados Individuales
          </button>
        </div>

        {/* Category Filter Sidebar + Content */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Category Sidebar */}
          {categories.length > 0 && (
            <div className="w-full md:w-64 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-lg p-4 sticky top-4">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <span>🗂️</span>
                  Filtrar por Categoría
                </h3>
                
                <div className="space-y-1">
                  {/* All Categories */}
                  <button
                    onClick={() => setCategoryFilter(null)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition ${
                      categoryFilter === null
                        ? 'bg-indigo-100 text-indigo-700 font-semibold'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    📋 Todas las actividades
                  </button>

                  {/* Root Categories */}
                  {categories.filter(cat => !cat.parent_id).map(category => (
                    <div key={category.id}>
                      <button
                        onClick={() => setCategoryFilter(category.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition flex items-center gap-2 ${
                          categoryFilter === category.id
                            ? 'bg-indigo-100 text-indigo-700 font-semibold'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                        style={{ borderLeft: `4px solid ${category.color}` }}
                      >
                        <span>{category.icon}</span>
                        <span className="text-sm">{category.name}</span>
                      </button>

                      {/* Subcategories */}
                      {categories.filter(sub => sub.parent_id === category.id).map(subcat => (
                        <button
                          key={subcat.id}
                          onClick={() => setCategoryFilter(subcat.id)}
                          className={`w-full text-left px-3 py-2 pl-8 rounded-lg transition flex items-center gap-2 ${
                            categoryFilter === subcat.id
                              ? 'bg-indigo-100 text-indigo-700 font-semibold'
                              : 'hover:bg-gray-100 text-gray-600'
                          }`}
                          style={{ borderLeft: `4px solid ${subcat.color}` }}
                        >
                          <span className="text-sm">{subcat.icon}</span>
                          <span className="text-xs">{subcat.name}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 min-w-0">
        {/* Tabs - Grid en mobile, horizontal en desktop */}
        <div className="mb-6">
          {/* Mobile: Grid 2x2 */}
          <div className="grid grid-cols-2 gap-2 sm:hidden">
            <button
              onClick={() => setActiveTab('quizzes')}
              className={`px-3 py-2 rounded-lg font-semibold transition text-xs ${
                activeTab === 'quizzes'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-lg">📝</span>
                <span>Quizzes</span>
                <span className="text-xs opacity-75">({filteredQuizzes.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('hangman')}
              className={`px-3 py-2 rounded-lg font-semibold transition text-xs ${
                activeTab === 'hangman'
                  ? 'bg-pink-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-lg">🎯</span>
                <span>Hangman</span>
                <span className="text-xs opacity-75">({filteredHangman.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('terminal')}
              className={`px-3 py-2 rounded-lg font-semibold transition text-xs ${
                activeTab === 'terminal'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-lg">💻</span>
                <span>Terminal</span>
                <span className="text-xs opacity-75">({filteredQuests.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('texthunt')}
              className={`px-3 py-2 rounded-lg font-semibold transition text-xs ${
                activeTab === 'texthunt'
                  ? 'bg-orange-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-lg">📚</span>
                <span>TextHunt</span>
                <span className="text-xs opacity-75">({filteredTextHunts.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('odoo')}
              className={`px-3 py-2 rounded-lg font-semibold transition text-xs ${
                activeTab === 'odoo'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-lg">🏢</span>
                <span>Odoo</span>
                <span className="text-xs opacity-75">({filteredOdooActivities.length})</span>
              </div>
            </button>
          </div>
          
          {/* Desktop: Horizontal tabs */}
          <div className="hidden sm:flex gap-3 md:gap-4">
            <button
              onClick={() => setActiveTab('quizzes')}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold transition whitespace-nowrap text-sm md:text-base ${
                activeTab === 'quizzes'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              📝 Quizzes ({filteredQuizzes.length})
            </button>
            <button
              onClick={() => setActiveTab('hangman')}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold transition whitespace-nowrap text-sm md:text-base ${
                activeTab === 'hangman'
                  ? 'bg-pink-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              🎯 Hangman ({filteredHangman.length})
            </button>
            <button
              onClick={() => setActiveTab('terminal')}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold transition whitespace-nowrap text-sm md:text-base ${
                activeTab === 'terminal'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              💻 Terminal Quest ({filteredQuests.length})
            </button>
            <button
              onClick={() => setActiveTab('texthunt')}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold transition whitespace-nowrap text-sm md:text-base ${
                activeTab === 'texthunt'
                  ? 'bg-orange-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              📚 TextHunt ({filteredTextHunts.length})
            </button>
            <button
              onClick={() => setActiveTab('odoo')}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold transition whitespace-nowrap text-sm md:text-base ${
                activeTab === 'odoo'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              🏢 Odoo ERP ({filteredOdooActivities.length})
            </button>
          </div>
        </div>

        {/* Quizzes List */}
        {activeTab === 'quizzes' && (
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Mis Quizzes</h3>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="mt-4 text-gray-600">Cargando quizzes...</p>
            </div>
          ) : filteredQuizzes.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm" data-testid="no-quizzes-message">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {courseFilter !== 'all' ? 'No hay quizzes en este curso' : 'No tienes quizzes aún'}
              </h3>
              <p className="text-gray-500 mb-6">
                {courseFilter !== 'all' ? 'Cambia el filtro de curso o crea un nuevo quiz' : 'Crea tu primer quiz para comenzar'}
              </p>
              <button
                onClick={() => navigate('/quiz/create')}
                className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-purple-700 transition"
              >
                Crear Mi Primer Quiz
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredQuizzes.map((quiz) => (
                <div key={quiz.id} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all p-6 border border-gray-200" data-testid={`quiz-card-${quiz.id}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-lg font-bold text-gray-800">{quiz.title}</h4>
                        {!quiz.is_public && quiz.assigned_courses?.length > 0 && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                            🔒 Privado
                          </span>
                        )}
                        {quiz.is_public && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                            🌐 Público
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{quiz.description || 'Sin descripción'}</p>
                      
                      {/* Course tags */}
                      {!quiz.is_public && quiz.assigned_courses?.length > 0 && courses.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {quiz.assigned_courses.slice(0, 3).map(courseId => {
                            const course = courses.find(c => c.id === courseId);
                            return course ? (
                              <span key={courseId} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded border border-purple-200">
                                📚 {course.name.length > 20 ? course.name.substring(0, 20) + '...' : course.name}
                              </span>
                            ) : null;
                          })}
                          {quiz.assigned_courses.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                              +{quiz.assigned_courses.length - 3} más
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{quiz.questions?.length || 0} preguntas</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => handleStartSession(quiz.id)}
                      className="flex-1 min-w-[100px] px-4 py-2 bg-success text-white rounded-lg font-semibold hover:bg-green-600 transition flex items-center justify-center gap-2"
                      data-testid={`start-session-btn-${quiz.id}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Iniciar
                    </button>
                    <button
                      onClick={() => {
                        setLeaderboardData({ type: 'quiz', id: quiz.id, title: quiz.title });
                        setShowLeaderboard(true);
                      }}
                      className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition"
                      data-testid={`leaderboard-btn-${quiz.id}`}
                      title="Ver Clasificación"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleExportXML(quiz.id, quiz.title)}
                      className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition"
                      data-testid={`export-xml-btn-${quiz.id}`}
                      title="Exportar a XML"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => navigate(`/quiz/edit/${quiz.id}`)}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                      data-testid={`edit-quiz-btn-${quiz.id}`}
                      title="Editar quiz"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteQuiz(quiz.id)}
                      className="px-4 py-2 bg-danger text-white rounded-lg hover:bg-red-600 transition"
                      data-testid={`delete-quiz-btn-${quiz.id}`}
                      title="Eliminar quiz"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        )}

        {/* Hangman Activities List */}
        {activeTab === 'hangman' && (
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Mis Ahorcados</h3>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="mt-4 text-gray-600">Cargando...</p>
              </div>
            ) : filteredHangman.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                <div className="text-6xl mb-4">🎯</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {courseFilter !== 'all' ? 'No hay ahorcados en este curso' : 'Aún no has creado ningún ahorcado'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {courseFilter !== 'all' ? 'Cambia el filtro de curso o crea un nuevo ahorcado' : 'Crea tu primera actividad de ahorcado'}
                </p>
                <button
                  onClick={() => navigate('/hangman/create')}
                  className="px-6 py-3 bg-pink-600 text-white rounded-lg font-semibold hover:bg-pink-700 transition"
                >
                  Crear Ahorcado
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredHangman.map((activity) => (
                  <div
                    key={activity.id}
                    className="bg-white rounded-xl shadow-sm p-4 sm:p-6 hover:shadow-md transition"
                  >
                    {/* Responsive layout: vertical on mobile, horizontal on desktop */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h4 className="text-lg sm:text-xl font-bold text-gray-800">{activity.title}</h4>
                          {!activity.is_public && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                              🔒 Privado
                            </span>
                          )}
                          {activity.is_public && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                              🌐 Público
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm mb-3">{activity.description || 'Sin descripción'}</p>
                        
                        {/* Course tags */}
                        {!activity.is_public && activity.assigned_courses?.length > 0 && courses.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {activity.assigned_courses.slice(0, 3).map(courseId => {
                              const course = courses.find(c => c.id === courseId);
                              return course ? (
                                <span key={courseId} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded border border-purple-200">
                                  📚 {course.name.length > 20 ? course.name.substring(0, 20) + '...' : course.name}
                                </span>
                              ) : null;
                            })}
                            {activity.assigned_courses.length > 3 && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                +{activity.assigned_courses.length - 3} más
                              </span>
                            )}
                          </div>
                        )}
                        
                        <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                          <span>📝 {activity.words?.length || 0} palabras</span>
                          <span>💔 {activity.lives || 6} vidas</span>
                          <span>⏱️ {activity.time_per_word || 60}s por palabra</span>
                          <span>⭐ {activity.points_per_word || 100} puntos</span>
                        </div>
                      </div>
                      
                      {/* Action buttons - bottom on mobile, right on desktop */}
                      <div className="flex gap-2">
                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleReorderHangman(activity.id, 'up')}
                            className="px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition text-xs"
                            title="Mover arriba"
                            disabled={filteredHangman[0]?.id === activity.id}
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => handleReorderHangman(activity.id, 'down')}
                            className="px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition text-xs"
                            title="Mover abajo"
                            disabled={filteredHangman[filteredHangman.length - 1]?.id === activity.id}
                          >
                            ↓
                          </button>
                        </div>
                        
                        {/* Main action buttons */}
                        <div className="flex flex-wrap gap-2 sm:flex-col sm:flex-nowrap">
                          <button
                            onClick={() => navigate(`/hangman/edit/${activity.id}`)}
                            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm"
                            title="Editar"
                          >
                            <span className="sm:hidden">✏️ Editar</span>
                            <svg className="hidden sm:block w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleExportHangmanXML(activity.id)}
                            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-sm"
                            title="Exportar a XML"
                          >
                            <span className="sm:hidden">📥 Exportar</span>
                            <svg className="hidden sm:block w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </button>
                          <button
                            onClick={() => navigate(`/hangman/${activity.id}/rankings`)}
                            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition text-sm"
                            title="Ver Ranking"
                          >
                            <span className="sm:hidden">🏆 Ranking</span>
                            <svg className="hidden sm:block w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteHangman(activity.id)}
                            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-danger text-white rounded-lg hover:bg-red-600 transition text-sm"
                            title="Eliminar"
                          >
                            <span className="sm:hidden">🗑️ Eliminar</span>
                            <svg className="hidden sm:block w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Terminal Quest Tab */}
        {activeTab === 'terminal' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Terminal Quest</h2>

            {filteredQuests.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">💻</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {courseFilter !== 'all' ? 'No hay Terminal Quests en este curso' : 'No hay Terminal Quests'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {courseFilter !== 'all' 
                    ? 'Cambia el filtro de curso o crea un nuevo Terminal Quest desde la sección "Crear Nueva Actividad"' 
                    : 'Crea tu primer Terminal Quest desde la sección "Crear Nueva Actividad" arriba'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredQuests.map((quest) => (
                  <div key={quest.id} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition">
                    {/* Responsive layout: vertical on mobile, horizontal on desktop */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-800">{quest.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{quest.description || 'Sin descripción'}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                          <span>🎯 {quest.levels?.length || 0} niveles</span>
                          <span>📅 {new Date(quest.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      
                      {/* Action buttons - bottom on mobile, right on desktop */}
                      <div className="flex flex-wrap gap-2 sm:flex-nowrap">
                        <button
                          onClick={() => navigate(`/terminal-quest/${quest.id}/progress`)}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition text-sm"
                          title="Ver Progreso"
                        >
                          <span className="sm:hidden">📊 Progreso</span>
                          <svg className="hidden sm:block w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            setLeaderboardData({ type: 'terminal', id: quest.id, title: quest.title });
                            setShowLeaderboard(true);
                          }}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition text-sm"
                          title="Ver Clasificación"
                        >
                          <span className="sm:hidden">🏆 Ranking</span>
                          <span className="hidden sm:inline">🏆</span>
                        </button>
                        <button
                          onClick={() => handleExportLinuxQuestXML(quest.id)}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-sm"
                          title="Exportar XML"
                        >
                          <span className="sm:hidden">📥 Exportar</span>
                          <svg className="hidden sm:block w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => navigate(`/terminal-quest/edit/${quest.id}`)}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm"
                          title="Editar"
                        >
                          <span className="sm:hidden">✏️ Editar</span>
                          <svg className="hidden sm:block w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteLinuxQuest(quest.id)}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-danger text-white rounded-lg hover:bg-red-600 transition text-sm"
                          title="Eliminar"
                        >
                          <span className="sm:hidden">🗑️ Eliminar</span>
                          <svg className="hidden sm:block w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TextHunt List */}
        {activeTab === 'texthunt' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Mis TextHunts 📚</h2>
            {loading ? (
              <p>Cargando...</p>
            ) : filteredTextHunts.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {courseFilter !== 'all' ? 'No hay TextHunts en este curso' : 'No hay TextHunts'}
                </h3>
                <p className="text-gray-500">
                  {courseFilter !== 'all' 
                    ? 'Cambia el filtro de curso o crea una nueva actividad de comprensión lectora' 
                    : 'Crea tu primera actividad de comprensión lectora'}
                </p>
                <button
                  onClick={() => navigate('/texthunt/create')}
                  className="mt-4 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold"
                >
                  + Crear TextHunt
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredTextHunts.map((texthunt) => (
                  <div
                    key={texthunt.id}
                    className="bg-white border rounded-lg p-4 sm:p-6 hover:shadow-md transition"
                  >
                    {/* Responsive layout: vertical on mobile, horizontal on desktop */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800">{texthunt.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{texthunt.description}</p>
                        <div className="flex flex-wrap gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-500">
                          <span>📝 {texthunt.questions?.length || 0} preguntas</span>
                          <span className="hidden sm:inline">📖 Texto: {texthunt.text_content?.substring(0, 50)}...</span>
                          <span>{texthunt.auto_correct_ai ? '🤖 IA' : '✍️ Manual'}</span>
                        </div>
                      </div>
                      
                      {/* Action buttons - bottom on mobile, right on desktop */}
                      <div className="flex flex-wrap gap-2 sm:flex-nowrap">
                        <button
                          onClick={() => navigate(`/texthunt/${texthunt.id}/play`)}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition text-sm"
                          title="Jugar"
                        >
                          <span className="sm:hidden">▶️ Jugar</span>
                          <span className="hidden sm:inline">▶️</span>
                        </button>
                        <button
                          onClick={() => navigate(`/texthunt/${texthunt.id}/results`)}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm"
                          title="Ver Resultados"
                        >
                          <span className="sm:hidden">📊 Resultados</span>
                          <svg className="hidden sm:block w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            setLeaderboardData({ type: 'texthunt', id: texthunt.id, title: texthunt.title });
                            setShowLeaderboard(true);
                          }}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition text-sm"
                          title="Ver Clasificación"
                        >
                          <span className="sm:hidden">🏆 Ranking</span>
                          <span className="hidden sm:inline">🏆</span>
                        </button>
                        <button
                          onClick={() => navigate(`/texthunt/edit/${texthunt.id}`)}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-sm"
                          title="Editar"
                        >
                          <span className="sm:hidden">✏️ Editar</span>
                          <svg className="hidden sm:block w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleExportTextHunt(texthunt)}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition text-sm"
                          title="Exportar"
                        >
                          <span className="sm:hidden">📥 Exportar</span>
                          <svg className="hidden sm:block w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteTextHunt(texthunt.id)}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-danger text-white rounded-lg hover:bg-red-600 transition text-sm"
                          title="Eliminar"
                        >
                          <span className="sm:hidden">🗑️ Eliminar</span>
                          <svg className="hidden sm:block w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Odoo Activities */}
        {activeTab === 'odoo' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Mis Actividades Odoo 🏢</h2>
              <button
                onClick={() => navigate('/odoo-activity/create')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold text-sm"
              >
                + Nueva Actividad
              </button>
            </div>
            
            {loading ? (
              <p>Cargando...</p>
            ) : filteredOdooActivities.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay Actividades Odoo</h3>
                <p className="text-gray-500">Crea actividades prácticas de ERP para tus alumnos</p>
                <button
                  onClick={() => navigate('/odoo-activity/create')}
                  className="mt-4 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold"
                >
                  + Crear Actividad Odoo
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredOdooActivities.map((activity) => (
                  <div key={activity.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-lg font-semibold text-gray-800">{activity.title}</h3>
                          <span className={`px-2 py-1 rounded text-xs ${
                            activity.difficulty === 'beginner' ? 'bg-green-100 text-green-700' :
                            activity.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {activity.difficulty === 'beginner' ? 'Fácil' :
                             activity.difficulty === 'intermediate' ? 'Medio' : 'Difícil'}
                          </span>
                          {!activity.is_public && activity.assigned_courses?.length > 0 && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                              🔒 Privado
                            </span>
                          )}
                          {activity.is_public !== false && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                              🌐 Público
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{activity.description}</p>
                        
                        {/* Course tags */}
                        {!activity.is_public && activity.assigned_courses?.length > 0 && courses.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2 mt-2">
                            {activity.assigned_courses.slice(0, 3).map(courseId => {
                              const course = courses.find(c => c.id === courseId);
                              return course ? (
                                <span key={courseId} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded border border-purple-200">
                                  {course.name}
                                </span>
                              ) : null;
                            })}
                            {activity.assigned_courses.length > 3 && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                +{activity.assigned_courses.length - 3} más
                              </span>
                            )}
                          </div>
                        )}
                        
                        <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                          <span>🎯 {activity.objectives?.length || 0} objetivos</span>
                          <span>🏆 {activity.max_score} puntos</span>
                          {activity.time_limit_minutes > 0 && <span>⏱️ {activity.time_limit_minutes} min</span>}
                          {activity.is_verifiable === false && (
                            <span className="text-orange-600 font-medium" title={activity.verification_note}>
                              ⚠️ Verificación manual
                            </span>
                          )}
                        </div>
                        {activity.is_verifiable === false && activity.verification_note && (
                          <p className="text-xs text-orange-600 mt-1 bg-orange-50 p-2 rounded">
                            {activity.verification_note}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => navigate(`/odoo-activity/${activity.id}/monitor`)}
                          className="px-3 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 text-sm"
                        >
                          👁️ Monitorear
                        </button>
                        <button
                          onClick={() => navigate(`/odoo-activity/edit/${activity.id}`)}
                          className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                          title="Editar actividad"
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => handleDeleteOdooActivity(activity.id)}
                          className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
                        >
                          🗑️ Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
          </div>
        </div>
      </div>

      {/* AI Generator Modal */}
      {showAIModal && (
        <AIGeneratorModal
          user={user}
          activityType={aiActivityType}
          onClose={() => setShowAIModal(false)}
          onSuccess={handleAISuccess}
        />
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <CategoryManager
          user={user}
          categories={categories}
          onUpdate={() => {
            fetchCategories();
            fetchAllActivities();
          }}
          onClose={() => setShowCategoryManager(false)}
          />
      )}    
      {/* Activity Leaderboard Modal */}
      {showLeaderboard && leaderboardData && leaderboardData.type !== 'terminal' && (
        <ActivityLeaderboard
          activityType={leaderboardData.type}
          activityId={leaderboardData.id}
          activityTitle={leaderboardData.title}
          onClose={() => {
            setShowLeaderboard(false);
            setLeaderboardData(null);
          }}
        />
      )}

      {/* Terminal Quest Leaderboard Modal */}
      {showLeaderboard && leaderboardData && leaderboardData.type === 'terminal' && (
        <TerminalQuestLeaderboard
          questId={leaderboardData.id}
          questTitle={leaderboardData.title}
          onClose={() => {
            setShowLeaderboard(false);
            setLeaderboardData(null);
          }}
        />
      )}
    </div>
  );
}

export default TeacherDashboard;
